import { useEffect, useRef, useState } from 'react'
import {
  ExportedFrame,
  frameToFile,
  zipDesignFiles,
} from 'components/VisualStudioPlayground/VisualEditor/utils/export/exportDesign'
import { useRootStore } from 'stores/RootStore'
import { useCompleteDesignAction } from 'components/Design/utils/useCompleteDesignAction'
import { AIJobStatus, DesignActionType } from 'types'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { DesignPreview } from 'api/clients/DesignClient'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { frontendOrigin } from 'utils/frontendOrigin'
import { downloadFile, downloadVideo } from 'components/VisualStudioPlayground/VisualEditor/utils/export/downloadFile'
import { useModals } from 'almanac-ui/Modal'
import copy from 'copy-to-clipboard'
import { useCreateExportSync } from 'hooks/queries/useCreateExportSync'
import { ExportFormatEnum } from 'components/ExportModal/Types'
import { getOrderedFrames } from 'components/VisualStudioPlayground/Shapes/FrameShape/helpers'

export const useDesignDownload = ({ fileId, filename }: { fileId?: string; filename?: string }) => {
  const { isMobile } = useResponsiveDesign()
  const { closeModal } = useModals()
  const { designStore, drawersStore, files: filesStore } = useRootStore()
  const [exportFiles, setExportFiles] = useState<ExportedFrame[]>([])
  const [previews, setPreviews] = useState<DesignPreview[]>([])
  const [isDownloading, setIsDownloading] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const completeActionMutation = useCompleteDesignAction()
  const { mutateAsync: createExport } = useCreateExportSync()
  const abortExport = useRef(new AbortController())
  const exportTitleRef = useRef<string | undefined>(filename)

  const designFileId = fileId || designStore?.file?.id
  const isActiveDesignStore = !!designStore && designStore.fileId === designFileId

  const resolveExportTitle = async () => {
    if (filename) return filename

    // Only trust designStore when it matches the file being exported.
    // Otherwise a stale store (e.g. previous workspace/file) leaks into the download name.
    if (isActiveDesignStore && designStore.file?.title) {
      return designStore.file.title
    }

    if (!designFileId) return undefined

    const cached = filesStore.get(designFileId)
    if (cached?.title) return cached.title

    try {
      const fetched = await filesStore.fetch(designFileId)
      return fetched.title
    } catch {
      return undefined
    }
  }

  useEffect(() => {
    fetchPreviews()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch the previews from the server
  const fetchPreviews = async () => {
    if (!designFileId) return null

    abortExport.current = new AbortController()

    setIsLoading(true)

    const fileExport = await createExport({
      almanacFileId: designFileId,
      exportFormat: ExportFormatEnum.DesignMedia,
      signal: abortExport.current.signal,
    })

    if (fileExport.status !== AIJobStatus.Successful || !fileExport.output) {
      showErrorMessageToast('There was an error generating your downloads, please try again later.')
      setIsLoading(false)
      isMobile ? drawersStore.closeDrawer() : closeModal()
    } else {
      setPreviews(fileExport.output)
      prepareFilesForExport(fileExport.output)
    }
  }

  // Prepare the files for download
  const prepareFilesForExport = async (previews: DesignPreview[]) => {
    const resolvedTitle = await resolveExportTitle()
    exportTitleRef.current = resolvedTitle

    const fileForExport = (
      await Promise.all(
        previews?.map(async (preview, index) => {
          const frameId = preview.id.split(':')[1]
          const fileType = preview.mime_type.split('/')[0]
          const fileFormat = preview.mime_type.split('/')[1].replace('quicktime', 'mov')

          let retryCount = 0
          let blob: Blob | null = null

          // Retry fetching the file 3 times if it's empty
          while (retryCount < 3 && (!blob || blob.size === 0)) {
            if (retryCount > 0) {
              await new Promise((resolve) => setTimeout(resolve, 1000 * retryCount))
            }

            const response = await fetch(preview.url)
            blob = await response.blob()
            retryCount++
          }

          if (!blob || blob.size === 0) {
            return null
          }

          const indexExt = previews.length > 1 ? `${index + 1}` : ''
          const fileTitle = resolvedTitle ? `${resolvedTitle}${indexExt}` : frameId
          let file = new File([blob], `${fileTitle}.${fileFormat}`)

          // Fallback to fetch the file from the design's frame in case the file generated from the preview's url is invalid
          if (file.size === 0 && isActiveDesignStore && designStore) {
            const frame = getOrderedFrames(designStore.editor).find((frame) => frame.id === `shape:${frameId}`)
            if (frame) {
              const frameFile = await frameToFile(designStore.editor, designStore.fileId, 'png', {}, frame)
              if (frameFile.type === 'video') {
                file = await downloadVideo(frameFile, `${fileTitle}`)
              } else {
                file = frameFile.file
              }
            }
          }

          return {
            frameId,
            file,
            designKey: designFileId,
            mime_type: preview.mime_type,
            type: fileType,
          } as ExportedFrame
        }),
      )
    ).filter((file): file is ExportedFrame => file !== null)

    // Only set loading to false if we have valid files
    if (fileForExport.length > 0) {
      setIsLoading(false)
      setExportFiles(fileForExport)
    } else {
      reportError('Failed to prepare files for download, files are empty')
      showErrorMessageToast('Failed to prepare files for download. Please try again.')
    }
  }

  const handleShare = () => {
    const shareFile =
      (designFileId && filesStore.get(designFileId)) || (isActiveDesignStore ? designStore?.file : undefined)
    if (!shareFile) return

    const url = `${frontendOrigin()}${shareFile.url}`

    if (isMobile) {
      const shareData = {
        title: shareFile.title,
        url,
      }
      navigator.share(shareData)
    } else {
      copy(url)
      showToastMessage('Linked copied', 'success')
    }
  }

  const handleSingleDownload = async (fileToDownload: ExportedFrame) => {
    setIsDownloading(true)
    const dismissToast = showToastMessage('Preparing download...', 'loading', undefined, 60)

    try {
      await window.navigator.share({
        files: [fileToDownload.file],
      })
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return
      downloadFile(fileToDownload.file)
    } finally {
      setIsDownloading(false)
      dismissToast()
    }
  }

  const cancelDownload = () => {
    abortExport.current.abort()
  }

  const handleSubmit = async (options?: { closeOnComplete: boolean }) => {
    const { closeOnComplete = true } = options ?? {}
    const dismissToast = showToastMessage('Preparing download...', 'loading', undefined, 60)
    setIsDownloading(true)

    let files
    if (exportFiles.length === 1) {
      files = [exportFiles[0].file]
    } else {
      files = exportFiles.map((exportedFile) => exportedFile.file)
    }

    completeActionMutation.mutate({ action: DesignActionType.Download })

    try {
      await window.navigator.share({
        files,
      })
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return
      if (files.length === 1) {
        downloadFile(files[0])
      } else {
        const zip = await zipDesignFiles(files, exportTitleRef.current)
        downloadFile(zip)
      }
    } finally {
      setIsDownloading(false)
      dismissToast()
    }

    if (closeOnComplete) {
      isMobile ? drawersStore.closeAllDrawers() : closeModal()
    }
  }

  const handleCopyToClipboard = () => {
    if (!isActiveDesignStore || !designStore) return

    if (!!designStore.captionsStore.caption?.length) {
      const clipboardItem = new ClipboardItem({
        'text/plain': new Blob([designStore.captionsStore.caption], { type: 'text/plain' }),
      })
      navigator.clipboard.write([clipboardItem]).then(() => {
        showToastMessage('Post content copied to your clipboard!')
      })
      return
    }
  }

  const framesCount = isActiveDesignStore
    ? getOrderedFrames(designStore.editor).length
    : Math.max(previews.length, exportFiles.length, 1)

  return {
    isDownloading,
    handleSubmit,
    handleCopyToClipboard,
    handleShare,
    previews,
    exportFiles,
    handleSingleDownload,
    isLoading,
    cancelDownload,
    framesCount,
  }
}
